/**
 * Workspace Redux Slice (Migrated to new TypeScript services)
 *
 * Manages workspace list state with normalized data structure
 * for optimal performance and caching.
 *
 * Features:
 * - Normalized entity storage
 * - Optimistic updates
 * - Error and loading states per operation
 * - Automatic cache timestamp tracking
 */

import {
  createSlice,
  createSelector,
  createAsyncThunk,
  type PayloadAction,
} from "@reduxjs/toolkit";
// Import from the new TypeScript services
import {
  workspaceService,
  type WorkspaceWithGroups,
} from "../../services/workspaceService";
import { noteService } from "../../services/noteService";
import { cache, selectedWorkspaceKey } from "../../utils/cacheUtils";
import { errorMessage } from "@jkast90/kast-common/format";
import type {
  NormalizedGroup,
  NormalizedTile,
  NormalizedWorkspace,
  Note,
} from "../../types/entities";
import type { RootState } from "../store";
import { loadLinks } from "./linkSlice";
import { logger } from "@common/utils/logger";

/** A workspace in the lean list (WorkspaceSelector's options). */
export type WorkspaceSummary = Pick<NormalizedWorkspace, "id" | "name"> &
  Partial<NormalizedWorkspace>;

interface OperationState {
  loading: boolean;
  error: string | null;
}

export interface WorkspaceState {
  // Compatibility with existing code
  workspaces: WorkspaceSummary[];
  groups: NormalizedGroup[];
  selectedWorkspace: WorkspaceSummary | null;
  apiUrl: string | null;
  allWorkspaces: WorkspaceWithGroups[];
  workspaceGroups: NormalizedGroup[];
  links: NormalizedTile[];
  allNotes: Note[];
  loading: boolean;
  error: string | null;

  // New normalized structure (for enhanced caching)
  entities: Record<number, WorkspaceSummary>;
  ids: number[];

  // Metadata
  lastFetched: number | null;
  cacheValid: boolean;

  // Operation-specific loading/error states
  operations: {
    list: OperationState;
    details: OperationState;
    create: OperationState;
    update: OperationState;
    delete: OperationState;
  };
}

interface ThunkConfig {
  state: RootState;
  rejectValue: string | null;
}

/** The lean list entry for a workspace with its groups. */
const summarize = (w: WorkspaceWithGroups): WorkspaceSummary => ({
  id: w.id,
  name: w.name,
});

// Async thunks for compatibility with existing code
export const loadWorkspaces = createAsyncThunk<
  NormalizedWorkspace[],
  void,
  ThunkConfig
>("workspace/loadWorkspaces", async (_, { dispatch, rejectWithValue }) => {
  try {
    // withCache returns cached data synchronously and revalidates in the
    // background — onFreshData lets us update state without a loading flash.
    return await workspaceService.getWorkspaces((fresh) => {
      dispatch(workspaceSlice.actions.setWorkspaces(fresh));
    });
  } catch (error) {
    return rejectWithValue(errorMessage(error));
  }
});

export const loadAllWorkspaces = createAsyncThunk<
  WorkspaceWithGroups[],
  { forceRefresh?: boolean } | undefined,
  ThunkConfig
>(
  "workspace/loadAllWorkspaces",
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const { forceRefresh } = payload || {};
      const result = await workspaceService.getAllWorkspacesWithGroups({
        forceRefresh,
        onFreshData: (fresh) => {
          dispatch(workspaceSlice.actions.setAllWorkspaces(fresh));
        },
      });
      return result;
    } catch (error) {
      return rejectWithValue(errorMessage(error));
    }
  },
);

// This action syncs groups from groupSlice to workspaceSlice for relationship calculations
export const syncGroupsFromGroupSlice = createAsyncThunk<
  NormalizedGroup[],
  void,
  ThunkConfig
>("workspace/syncGroupsFromGroupSlice", async (_, { getState }) => {
  // Get groups from the groupSlice state
  const state = getState();
  return state.groups.groups;
});

export const loadAllNotes = createAsyncThunk<Note[], void, ThunkConfig>(
  "workspace/loadAllNotes",
  async (_, { rejectWithValue }) => {
    try {
      // Fetch all notes directly from the notes endpoint
      return await noteService.getNotes();
    } catch (error) {
      return rejectWithValue(errorMessage(error));
    }
  },
);

/**
 * @deprecated Use loadLinks from linkSlice directly via useReduxLinks hook.
 * Links should only be stored in linkSlice to avoid duplicates.
 * This thunk is kept for backward compatibility but will be removed.
 */
export const loadAllLinks = createAsyncThunk<
  NormalizedTile[],
  void,
  ThunkConfig
>("workspace/loadAllLinks", async (_, { dispatch, rejectWithValue }) => {
  try {
    // Delegate to linkSlice - the only source of truth for links
    await dispatch(loadLinks()).unwrap();
    // Return empty array - links are now only in linkSlice
    return [];
  } catch (error) {
    return rejectWithValue(errorMessage(error));
  }
});

// The signed-in user's selected workspace, kept under their own key so it
// survives signing out and back in without leaking to the next person.
const isWorkspaceSummary = (value: unknown): value is WorkspaceSummary =>
  typeof value === "object" && value !== null && "id" in value;

const loadSelectedWorkspaceFromStorage = (): WorkspaceSummary | null => {
  try {
    const stored = localStorage.getItem(selectedWorkspaceKey());
    if (stored) {
      const parsed: unknown = JSON.parse(stored);
      return isWorkspaceSummary(parsed) ? parsed : null;
    }
  } catch (error) {
    logger.warn(
      "[workspaceSlice] Failed to load selected workspace from localStorage:",
      error,
    );
  }
  return null;
};

const buildEntitiesAndIds = (list: readonly (WorkspaceSummary | null)[]) => {
  const entities: Record<number, WorkspaceSummary> = {};
  const ids: number[] = [];
  list.forEach((ws) => {
    if (ws == null || ws.id == null) return;
    entities[ws.id] = ws;
    ids.push(ws.id);
  });
  return { entities, ids };
};

// Hydrate from the signed-in user's localStorage cache so cached workspaces
// render synchronously on page load instead of waiting for the API
// round-trip. Computed lazily, so a user change (which resets this slice)
// re-reads the new user's cache instead of keeping the first one's.
const buildInitialState = (): WorkspaceState => {
  const cachedAllWorkspaces =
    cache.get<WorkspaceWithGroups[]>("workspaces-with-groups", {
      ttl: Infinity,
    }) || [];
  // The lean `workspaces` list used to be populated by getWorkspaces() during
  // startup. After the loading refactor, only getAllWorkspacesWithGroups runs
  // — so we fall back to deriving the lean list from the fat cache so the
  // WorkspaceSelector dropdown has options to match selectedWorkspace against
  // on first paint. Without this fallback, the dropdown renders blank on cold
  // hydration even though selectedWorkspace is populated from localStorage.
  const cachedWorkspaces =
    cache.get<NormalizedWorkspace[]>("workspaces", { ttl: Infinity }) ||
    cachedAllWorkspaces.map(summarize);

  const cachedNorm = buildEntitiesAndIds(cachedWorkspaces);

  return {
    // Compatibility with existing code
    workspaces: cachedWorkspaces,
    groups: [],
    selectedWorkspace: loadSelectedWorkspaceFromStorage(),
    apiUrl: null,
    allWorkspaces: cachedAllWorkspaces,
    workspaceGroups: [],
    links: [],
    allNotes: [],
    loading: false,
    error: null,

    // New normalized structure (for enhanced caching)
    entities: cachedNorm.entities,
    ids: cachedNorm.ids,

    // Metadata
    lastFetched: null,
    cacheValid: cachedWorkspaces.length > 0 || cachedAllWorkspaces.length > 0,

    // Operation-specific loading/error states
    operations: {
      list: { loading: false, error: null },
      details: { loading: false, error: null },
      create: { loading: false, error: null },
      update: { loading: false, error: null },
      delete: { loading: false, error: null },
    },
  };
};

const workspaceSlice = createSlice({
  name: "workspace",
  initialState: buildInitialState,
  reducers: {
    setSelectedWorkspace: (
      state,
      action: PayloadAction<WorkspaceSummary | null>,
    ) => {
      state.selectedWorkspace = action.payload;
      // Persist to localStorage
      try {
        if (action.payload) {
          localStorage.setItem(
            selectedWorkspaceKey(),
            JSON.stringify(action.payload),
          );
        } else {
          localStorage.removeItem(selectedWorkspaceKey());
        }
      } catch (error) {
        logger.warn(
          "[workspaceSlice] Failed to save selected workspace to localStorage:",
          error,
        );
      }
    },
    setApiUrl: (state, action: PayloadAction<string | null>) => {
      state.apiUrl = action.payload;
    },
    // Used by background revalidation (onFreshData) to update state without
    // touching loading flags.
    setWorkspaces: (state, action: PayloadAction<WorkspaceSummary[]>) => {
      state.workspaces = action.payload;
      state.entities = {};
      state.ids = [];
      action.payload.forEach((ws) => {
        if (ws == null || ws.id == null) return;
        state.entities[ws.id] = ws;
        state.ids.push(ws.id);
      });
      state.lastFetched = Date.now();
      state.cacheValid = true;
    },
    setAllWorkspaces: (state, action: PayloadAction<WorkspaceWithGroups[]>) => {
      state.allWorkspaces = action.payload;
      // Always refresh the lean workspaces list from fresh data — see the
      // matching block in loadAllWorkspaces.fulfilled for the rationale.
      state.workspaces = action.payload.map(summarize);
    },
    clearError: (state) => {
      state.error = null;
      // Clear all operation errors
      Object.values(state.operations).forEach((op) => {
        op.error = null;
      });
    },
    updateWorkspaceInList: (
      state,
      action: PayloadAction<{
        workspaceId: number;
        updates: Partial<WorkspaceWithGroups>;
      }>,
    ) => {
      const { workspaceId, updates } = action.payload;

      // Update in normalized entities
      if (state.entities[workspaceId]) {
        state.entities[workspaceId] = {
          ...state.entities[workspaceId],
          ...updates,
        };
      }

      // Update in arrays for backward compatibility
      const workspaceIndex = state.workspaces.findIndex(
        (w) => w.id === workspaceId,
      );
      if (workspaceIndex !== -1) {
        state.workspaces[workspaceIndex] = {
          ...state.workspaces[workspaceIndex],
          ...updates,
        };
      }
      const allIndex = state.allWorkspaces.findIndex(
        (w) => w.id === workspaceId,
      );
      if (allIndex !== -1) {
        state.allWorkspaces[allIndex] = {
          ...state.allWorkspaces[allIndex],
          ...updates,
        };
      }
    },
    removeWorkspaceFromList: (state, action: PayloadAction<number>) => {
      const workspaceId = action.payload;

      // Remove from normalized structure
      delete state.entities[workspaceId];
      state.ids = state.ids.filter((id) => id !== workspaceId);

      // Remove from arrays for backward compatibility
      state.workspaces = state.workspaces.filter((w) => w.id !== workspaceId);
      state.allWorkspaces = state.allWorkspaces.filter(
        (w) => w.id !== workspaceId,
      );

      // Clear selection if deleted workspace was selected
      if (state.selectedWorkspace?.id === workspaceId) {
        state.selectedWorkspace = null;
        state.workspaceGroups = [];
      }
    },
    setCacheValidity: (state, action: PayloadAction<boolean>) => {
      state.cacheValid = action.payload;
      if (!action.payload) {
        state.lastFetched = null;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Load workspaces — only show loading on cold load. When we already
      // have data (from cache hydration or a prior fetch), revalidate silently.
      .addCase(loadWorkspaces.pending, (state) => {
        if (state.workspaces.length === 0) {
          state.loading = true;
          state.operations.list.loading = true;
        }
      })
      .addCase(loadWorkspaces.fulfilled, (state, action) => {
        state.loading = false;
        state.operations.list.loading = false;
        // A list load that works clears the last one's failure, which the
        // sign-in gate (ProtectedRoute) reads.
        state.operations.list.error = null;
        state.workspaces = action.payload;

        // Update normalized structure
        state.entities = {};
        state.ids = [];
        action.payload.forEach((workspace) => {
          state.entities[workspace.id] = workspace;
          state.ids.push(workspace.id);
        });

        state.lastFetched = Date.now();
        state.cacheValid = true;
      })
      .addCase(loadWorkspaces.rejected, (state, action) => {
        state.loading = false;
        state.operations.list.loading = false;
        state.error = action.payload ?? null;
        state.operations.list.error = action.payload ?? null;
      })

      // Load all workspaces — same cache-first pattern.
      .addCase(loadAllWorkspaces.pending, (state) => {
        if (state.allWorkspaces.length === 0 && state.workspaces.length === 0) {
          state.loading = true;
          state.operations.list.loading = true;
        }
      })
      .addCase(loadAllWorkspaces.fulfilled, (state, action) => {
        state.loading = false;
        state.operations.list.loading = false;
        // A list load that works clears the last one's failure, which the
        // sign-in gate (ProtectedRoute) reads.
        state.operations.list.error = null;
        state.allWorkspaces = action.payload;

        // Always refresh the lean `workspaces` array (used by WorkspaceSelector)
        // from the fresh fat response. Without this, deletions/renames don't
        // propagate to the dropdown until the next mutation.
        state.workspaces = action.payload.map(summarize);
      })
      .addCase(loadAllWorkspaces.rejected, (state, action) => {
        state.loading = false;
        state.operations.list.loading = false;
        state.error = action.payload ?? null;
        state.operations.list.error = action.payload ?? null;
      })

      // Sync groups from groupSlice
      .addCase(syncGroupsFromGroupSlice.fulfilled, (state, action) => {
        state.groups = action.payload;
      })

      // Load all notes
      .addCase(loadAllNotes.pending, (state) => {
        state.loading = true;
      })
      .addCase(loadAllNotes.fulfilled, (state, action) => {
        state.loading = false;
        state.allNotes = action.payload;
      })
      .addCase(loadAllNotes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? null;
      })

      // Load all links
      .addCase(loadAllLinks.pending, (state) => {
        state.loading = true;
      })
      .addCase(loadAllLinks.fulfilled, (state, action) => {
        state.loading = false;
        state.links = action.payload;
      })
      .addCase(loadAllLinks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? null;
      });
  },
});

// Export actions
export const {
  setSelectedWorkspace,
  setApiUrl,
  setWorkspaces,
  setAllWorkspaces,
  clearError,
  updateWorkspaceInList,
  removeWorkspaceFromList,
  setCacheValidity,
} = workspaceSlice.actions;

// Export actions with workspaceActions namespace for compatibility with WorkspaceService.js
export const workspaceActions = workspaceSlice.actions;

// Selectors
export const selectWorkspaces = (state: RootState) =>
  state.workspace.workspaces;
export const selectAllWorkspaces = (state: RootState) =>
  state.workspace.allWorkspaces;
export const selectWorkspaceGroups = (state: RootState) =>
  state.workspace.workspaceGroups;
export const selectSelectedWorkspace = (state: RootState) =>
  state.workspace.selectedWorkspace;
export const selectWorkspaceError = (state: RootState) => state.workspace.error;
export const selectWorkspaceLoading = (state: RootState) =>
  state.workspace.loading;

// Memoized selectors for normalized data
export const selectWorkspaceEntities = (state: RootState) =>
  state.workspace.entities;
export const selectWorkspaceIds = (state: RootState) => state.workspace.ids;

export const selectWorkspaceById = createSelector(
  [
    selectWorkspaceEntities,
    (_state: RootState, workspaceId: number) => workspaceId,
  ],
  (entities, workspaceId) => entities[workspaceId],
);

export const selectNormalizedWorkspaces = createSelector(
  [selectWorkspaceEntities, selectWorkspaceIds],
  (entities, ids) => ids.map((id) => entities[id]),
);

// Cache validity selectors
export const selectCacheValid = (state: RootState) =>
  state.workspace.cacheValid;
export const selectLastFetched = (state: RootState) =>
  state.workspace.lastFetched;

const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

export const selectCacheExpired = createSelector(
  [selectLastFetched],
  (lastFetched) => {
    if (!lastFetched) return true;
    return Date.now() - lastFetched > CACHE_TTL;
  },
);

// Helper selector to determine if we should fetch workspaces
export const selectShouldFetchWorkspaces = (state: RootState) => {
  const hasWorkspaces = state.workspace.workspaces.length > 0;
  const cacheExpired = selectCacheExpired(state);
  return !hasWorkspaces || cacheExpired;
};

// Export the reducer
export default workspaceSlice.reducer;
